import {useEffect, useRef, useState} from 'react'
import {Banner} from '@primer/react'
import {StackAddIcon} from '@primer/octicons-react'
import {useQueryClient} from '@github-ui/react-query'
import {PreviewStackDialog} from './PreviewStackDialog'
import type {StackCreatedInfo} from './StackCreatedBanner'
import {useAnalytics, useClickAnalytics} from '@github-ui/use-analytics'
import {buildPullRequestUserActionContext} from '../../analytics/pull-request-user-action'
import {usePreviewStackQueryKey} from '../../page-data/loaders/use-preview-stack-data'

export type CanStackBannerProps = {
  pullRequestNumber: number
  onStackCreated: (info: StackCreatedInfo) => void
  onDismiss: () => void
}

export function CanStackBanner({pullRequestNumber, onStackCreated, onDismiss}: CanStackBannerProps) {
  const [isPreviewOpen, setIsPreviewOpen] = useState(false)
  const previewButtonRef = useRef<HTMLButtonElement>(null)
  const {sendClickAnalyticsEvent} = useClickAnalytics()
  const {sendAnalyticsEvent} = useAnalytics()
  const queryClient = useQueryClient()
  const previewStackQueryKey = usePreviewStackQueryKey()

  const labelText = 'Can Stack Banner'

  const onStackCreatedHandler = (info: StackCreatedInfo) => {
    onStackCreated(info)
    // Defer the preview_stack invalidation until after the current frame so
    // the dialog can finish closing before it refetches (and potentially
    // flashes a failure state) with an empty preview.
    window.requestAnimationFrame(() => {
      queryClient.invalidateQueries({queryKey: previewStackQueryKey})
    })
  }

  const handlePreviewClick = () => {
    sendClickAnalyticsEvent(
      buildPullRequestUserActionContext({
        category: 'stack_preview',
        action: 'open',
      }),
    )
    setIsPreviewOpen(true)
  }

  useEffect(() => {
    sendAnalyticsEvent(
      'can_stack_banner_render',
      undefined,
      buildPullRequestUserActionContext({
        category: 'stack_preview',
        action: 'view',
      }),
    )
  }, [sendAnalyticsEvent])

  return (
    <>
      <Banner
        aria-label={labelText}
        title={labelText}
        hideTitle
        description="This pull request can be stacked with other pull requests"
        variant="info"
        leadingVisual={<StackAddIcon />}
        onDismiss={onDismiss}
        primaryAction={
          <Banner.PrimaryAction ref={previewButtonRef} onClick={handlePreviewClick}>
            Preview stack
          </Banner.PrimaryAction>
        }
      />
      {isPreviewOpen && (
        <PreviewStackDialog
          currentPullNumber={pullRequestNumber}
          returnFocusRef={previewButtonRef}
          onClose={() => setIsPreviewOpen(false)}
          onStackCreated={onStackCreatedHandler}
        />
      )}
    </>
  )
}
